// Фасад админ-консоли (аудит, этап 2 — декомпозиция). Вкладки разнесены по
// внутренним модулям: admin-stats (обзор), admin-users, admin-categories,
// admin-logs (аудит/отладка), admin-journey-settings; общие хелперы —
// admin-shared. Здесь остаются только маршрутизация вкладок (initAdminTabs)
// и ре-экспорт публичного API (auth.ts / main.ts ничего не заметили).
import { state } from '../shared/state';
import { $, showToast } from '../shared/index';
import { loadAdminKb, initAdminKb, stopAdminKbPolling } from './admin-kb';
import { loadAdminStats, loadAvgVideoCacheState, clearAvgVideoCacheState } from './admin-stats';
import { loadAdminUsers } from './admin-users';
import { loadAdminCategories } from './admin-categories';
import { loadAuditLogs, loadDebugLogs } from './admin-logs';
import { loadAdminJourneySettings, bindAdminJourneyEvents } from './admin-journey-settings';

export { loadAdminStats } from './admin-stats';
export { loadAdminUsers } from './admin-users';
export { loadAdminCategories } from './admin-categories';
export { loadAuditLogs, appendDebugLog, loadDebugLogs } from './admin-logs';
export { loadAdminJourneySettings, bindAdminJourneyEvents } from './admin-journey-settings';

// Вкладка «Провайдеры» подгружается лениво; обработчики её кнопок вешаются один раз.
let providerEnvBound = false;
// То же для вкладки «Отложенная услуга» (OG-2).
let deferredBound = false;
let integrationBound = false;
// И для вкладки «Совет» (OG-4).
let councilBound = false;
// Вкладка «Эфир» лениво импортируется по клику и держит несколько setTimeout-опросов
// (job/stream/dual/dstream); при уходе со вкладки их надо гасить, иначе они бьют
// каждые 2.5-3с до перезагрузки страницы.
let currentAdminTab: string | undefined;

// --- Группы меню ---------------------------------------------------------
// Вкладок 16, поэтому в шапке они собраны в группы по смыслу: кнопка группы
// раскрывает список подчинённых вкладок. Сами вкладки остались .admin-tab —
// маршрутизация ниже их не различает, лежит вкладка в панели или в шапке.

function closeAdminTabGroups(except?: Element) {
  document.querySelectorAll('.admin-tab-group.open').forEach(group => {
    if (group === except) return;
    group.classList.remove('open');
    group.querySelector('.admin-tab-group-btn')?.setAttribute('aria-expanded', 'false');
  });
}

// Подчёркиваем ту группу, внутри которой лежит открытая вкладка.
function syncAdminTabGroupState() {
  document.querySelectorAll('.admin-tab-group').forEach(group => {
    group.classList.toggle('has-active', !!group.querySelector('.admin-tab.active'));
  });
}

// Панель крайней правой группы уезжает за край окна — прижимаем её вправо.
function alignAdminTabGroupPanel(group: Element) {
  const panel = group.querySelector<HTMLElement>('.admin-tab-group-panel');
  if (!panel) return;
  panel.classList.remove('align-right');
  if (panel.getBoundingClientRect().right > document.documentElement.clientWidth - 8) {
    panel.classList.add('align-right');
  }
}

function openAdminTabGroup(group: Element, open: boolean) {
  closeAdminTabGroups(open ? group : undefined);
  group.classList.toggle('open', open);
  group.querySelector('.admin-tab-group-btn')?.setAttribute('aria-expanded', String(open));
  if (open) alignAdminTabGroupPanel(group);
}

function initAdminTabGroups() {
  document.querySelectorAll('.admin-tab-group-btn').forEach(btn => {
    const group = btn.closest('.admin-tab-group');
    if (!group) return;

    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      openAdminTabGroup(group, !group.classList.contains('open'));
    });

    // Enter и пробел браузер сам превращает в click — здесь только стрелка вниз,
    // иначе keydown откроет группу, а следом пришедший click её закроет.
    btn.addEventListener('keydown', (e) => {
      if ((e as KeyboardEvent).key !== 'ArrowDown') return;
      e.preventDefault();
      openAdminTabGroup(group, true);
      group.querySelector<HTMLElement>('.admin-tab-group-panel .admin-tab')?.focus();
    });
  });

  // Стрелки внутри панели ходят по пунктам, Esc возвращает фокус на шапку группы.
  document.querySelectorAll('.admin-tab-group-panel').forEach(panel => {
    panel.addEventListener('keydown', (e) => {
      const ev = e as KeyboardEvent;
      if (ev.key !== 'ArrowDown' && ev.key !== 'ArrowUp') return;
      ev.preventDefault();
      const items = Array.from(panel.querySelectorAll<HTMLElement>('.admin-tab'));
      const at = items.indexOf(document.activeElement as HTMLElement);
      const next = ev.key === 'ArrowDown' ? at + 1 : at - 1;
      items[(next + items.length) % items.length]?.focus();
    });
  });

  document.addEventListener('click', () => closeAdminTabGroups());

  document.addEventListener('keydown', (e) => {
    if ((e as KeyboardEvent).key !== 'Escape') return;
    const open = document.querySelector('.admin-tab-group.open');
    if (!open) return;
    closeAdminTabGroups();
    open.querySelector<HTMLElement>('.admin-tab-group-btn')?.focus();
  });

  syncAdminTabGroupState();
}

export function initAdminTabs() {
  bindAdminJourneyEvents();
  initAdminKb();
  initAdminTabGroups();

  document.querySelectorAll('.admin-tab').forEach(tab => {
    tab.addEventListener('click', () => {
      const el = tab as HTMLElement;
      const target = el.dataset.tab;
      if (currentAdminTab === 'avg-broadcast' && target !== 'avg-broadcast') {
        void import('./admin-avg-broadcast').then((m) => m.stopAdminAvgBroadcastPolling());
      }
      currentAdminTab = target;
      document.querySelectorAll('.admin-tab').forEach(t => {
        t.classList.toggle('active', t === tab);
        t.setAttribute('aria-selected', String(t === tab));
      });
      document.querySelectorAll('.admin-tab-content').forEach(c => {
        c.classList.toggle('active', c.id === `admin-tab-${target}`);
      });
      // Выбор пункта закрывает список и переносит подсветку на его группу.
      // Сюда же приходят программные переходы (admin-logs, admin-avg-dialog).
      closeAdminTabGroups();
      syncAdminTabGroupState();
      if (target === 'overview') loadAdminStats();
      else if (target === 'users') loadAdminUsers();
      else if (target === 'categories') loadAdminCategories();
      else if (target === 'journey') loadAdminJourneySettings();
      else if (target === 'examples') {
        void import('./admin-examples').then((m) => m.loadAdminExamples());
      }
      else if (target === 'avg-video') {
        void import('./admin-avg-video').then((m) => m.loadAdminAvgVideo());
        void import('./admin-avg-video-collections').then((m) => m.loadAdminAvgVideoCollections());
      }
      else if (target === 'video-infra') {
        // Реквизиты внешнего хранилища — своя вкладка (AV-ADR-2, решение 7).
        void import('./admin-avg-video').then((m) => m.loadAdminAvgVideoInfra());
      }
      else if (target === 'avg-dialog') {
        void import('./admin-avg-dialog').then((m) => m.loadAdminAvgDialog());
      }
      else if (target === 'avg-broadcast') {
        void import('./admin-avg-broadcast').then((m) => m.loadAdminAvgBroadcast());
      }
      else if (target === 'provider-env') {
        void import('./admin-provider-env').then((m) => {
          if (!providerEnvBound) {
            m.bindAdminProviderEnvEvents();
            providerEnvBound = true;
          }
          return m.loadAdminProviderEnv();
        });
      }
      else if (target === 'deferred') {
        void import('./admin-deferred').then((m) => {
          if (!deferredBound) {
            m.bindAdminDeferredEvents();
            deferredBound = true;
          }
          return m.loadAdminDeferred();
        });
      }
      else if (target === 'integration') {
        void import('./admin-integration').then((m) => {
          if (!integrationBound) {
            m.bindAdminIntegrationEvents();
            integrationBound = true;
          }
          return m.loadAdminIntegration();
        });
      }
      else if (target === 'council') {
        void import('./admin-council').then((m) => {
          if (!councilBound) {
            m.bindAdminCouncilEvents();
            councilBound = true;
          }
          return m.loadAdminCouncil();
        });
      }
      else if (target === 'kb') loadAdminKb();
      else if (target === 'vector-stores') {
        void import('./admin-vector-stores').then((m) => m.loadAdminVectorStores());
      }
      else if (target === 'audit') loadAuditLogs();
      else if (target === 'debug') loadDebugLogs();
    });
  });

  $('admin-user-search')?.addEventListener('input', (e: Event) => {
    const input = e.target as HTMLInputElement;
    const q = input.value.toLowerCase();
    document.querySelectorAll('.user-item').forEach(item => {
      const nameEl = item.querySelector('.user-item-name');
      const name = nameEl?.textContent?.toLowerCase() || '';
      (item as HTMLElement).style.display = name.includes(q) ? '' : 'none';
    });
  });

  $('btn-refresh-graph-stats')?.addEventListener('click', () => {
    void loadAdminStats();
  });

  // Горячее хранилище видео (AV-ADR-1, AV-HC-5) — строка состояния в «Обзоре».
  $('btn-avg-video-cache-refresh')?.addEventListener('click', () => {
    void loadAvgVideoCacheState();
  });

  $('btn-avg-video-cache-clear')?.addEventListener('click', () => {
    void clearAvgVideoCacheState();
  });

  $('btn-purge-graph-namespace')?.addEventListener('click', async () => {
    const graphRes = await fetch('/api/admin/semantic-graph/stats', {
      headers: { Authorization: 'Bearer ' + state.authToken },
    });
    if (!graphRes.ok) {
      showToast('❌ Не удалось получить namespace графа');
      return;
    }
    const graph = await graphRes.json() as { namespace?: string };
    const namespace = graph.namespace || '';
    if (!namespace) {
      showToast('❌ Namespace не определён');
      return;
    }
    if (!confirm(`Очистить Semantic Graph для namespace "${namespace}"? Потребуется re-index.`)) return;

    const r = await fetch(`/api/admin/semantic-graph/namespace/${encodeURIComponent(namespace)}`, {
      method: 'DELETE',
      headers: {
        Authorization: 'Bearer ' + state.authToken,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ confirm: true }),
    });
    if (r.ok) {
      showToast('✅ Semantic Graph очищен');
      void loadAdminStats();
    } else {
      const err = await r.json().catch(() => ({})) as { detail?: string };
      showToast('❌ ' + (err.detail || 'Ошибка очистки графа'));
    }
  });
}
